import { useState, useEffect, useRef } from "react";
import useWebSocket from "react-use-websocket";
import ParseJson from "utils/parseJson";

function isEmptyObjectPlaceholder(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  );
}

function parseSocketData(raw) {
  if (raw == null || raw === "") return null;
  const str = typeof raw === "string" ? raw : String(raw);
  try {
    return JSON.parse(str);
  } catch {
    const first = str.indexOf("{");
    const last = str.lastIndexOf("}");
    if (first >= 0 && last > first) {
      const slice = str.slice(first, last + 1);
      try {
        return JSON.parse(slice);
      } catch {
        const fallback = ParseJson(slice);
        if (fallback && !fallback.error) return fallback;
      }
    }
    const fallback = ParseJson(str);
    if (fallback && !fallback.error) return fallback;
    return null;
  }
}

function getOrdersResultRows(response) {
  if (!response || typeof response !== "object") return null;
  const rows = response.Result ?? response.result;
  return Array.isArray(rows) ? rows : null;
}

export const useWebsocketFetchOrders = ({ onSuccessOrders }) => {
  const [socketUrl, setSocketUrl] = useState(null);
  const [statusOrders, setStatus] = useState("off");
  const [dataOrders, setData] = useState([]);

  const dataOrdersRef = useRef([]);
  const isFetchingRef = useRef(false);

  const { sendJsonMessage, lastJsonMessage, lastMessage } = useWebSocket(socketUrl, {
    onOpen: () => {
      // console.log(`Connected to App WS orders`);
      setStatus("on");
      dataOrdersRef.current = [];
      setData([]);
    },
    onClose: () => {
      // console.log("close orders");
      const latest = dataOrdersRef.current;
      if (
        (latest?.length === 0 && isFetchingRef.current) ||
        (latest?.length === 1 &&
          (latest[0]?.MessageResponse === "Nenhum registro localizado." ||
            latest[0]?.MessageResponse ===
              "Falha em validação de filtros!"))
      ) {
        setStatus("empty");
      }

      if (latest.length !== 0) {
        const hasOnlyErrorMessage =
          latest.length === 1 &&
          (latest[0]?.MessageResponse === "Nenhum registro localizado." ||
            latest[0]?.MessageResponse === "Falha em validação de filtros!");
        if (!hasOnlyErrorMessage) {
          onSuccessOrders(latest);
          setStatus("off");
        }
      }
      isFetchingRef.current = false;
      dataOrdersRef.current = [];
      setData([]);
    },
    onError: () => {
      // console.log('erro');
      isFetchingRef.current = false;
      setStatus("error");
    },
    shouldReconnect: () => true,
    reconnectInterval: 2000,
  });

  const handleFetchDataOrders = (data) => {
    // console.log("data request orders", data);
    isFetchingRef.current = true;
    setStatus("fetching");
    sendJsonMessage(data);
  };

  const handleFetchUrlOrders = (url) => {
    setSocketUrl(url);
  };

  const onMessageReceive = (response) => {
    const rows = getOrdersResultRows(response);

    if (rows === null) {
      if (response?.message === "Forbidden") {
        isFetchingRef.current = false;
        setStatus("empty");
      }
      return;
    }

    const first = rows[0];

    if (
      first?.MessageResponse === "Nenhum registro localizado." ||
      first?.MessageResponse === "Falha em validação de filtros!"
    ) {
      isFetchingRef.current = false;
      setStatus("empty");
      return;
    }

    if (rows.length === 0) {
      isFetchingRef.current = false;
      setStatus("empty");
      return;
    }

    const next = [...dataOrdersRef.current, ...rows];
    dataOrdersRef.current = next;
    setData(next);
    setStatus("off");
    onSuccessOrders(next);
  };

  useEffect(() => {
    if (!lastMessage?.data) return;

    let response = lastJsonMessage;
    if (response == null || isEmptyObjectPlaceholder(response)) {
      response = parseSocketData(lastMessage.data);
    } else if (!getOrdersResultRows(response)) {
      const reparsed = parseSocketData(lastMessage.data);
      if (reparsed) response = reparsed;
    }

    if (response) {
      onMessageReceive(response);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage]);

  return {
    handleFetchDataOrders,
    handleFetchUrlOrders,
    dataOrders,
    statusOrders,
  };
};
